<script  lang="ts">
  import AddPlayer from "@frontend/Components/AddPlayer.svelte";
  import Modal from "@frontend/Components/Modal.svelte";
  import WithdrawDepositForm from "@frontend/Components/WithdrawDepositForm.svelte";
  import WithdrawCashierForm from "@frontend/Components/WithdrawCashierForm.svelte";
  import { service } from "@frontend/services";
  import { GetCachedToken, me, RefreshMe } from "@frontend/user";
  import { type ApiResultOfPublicUserDTO, type User, type WalletTransaction, UserRole, type PublicUserDTO, WalletTransactionType } from "@frontend/walletapi";
  import { onMount } from "svelte";
  import { push, location } from "svelte-spa-router";
  import EditPlayerForm from "../Components/EditPlayerForm.svelte";
  import { formatDate, formatMoney } from "./Formats";
  import { usersByRole, RefreshUsers } from "@frontend/userlist";
  import { get } from "svelte/store";
  import { fade } from 'svelte/transition';
  import EditUserForm from "@frontend/Components/EditUserForm.svelte";
  import { Transactions } from "@frontend/Transactionlist";
  import PromotionalcreditsForm from "@frontend/Components/PromotionalcreditsForm.svelte";
  import { translate, currentLanguage } from "../Translator";
  import { groups } from "@frontend/grouplist";


  let currentPage = 1;
  let playersPerPage = 20;
  let paginatedPlayers: User[] = [];
  let lastKnownPage = 1; 
  let isNavigatingProgrammatically = false;

  let processedTransactionIds = new Set<number>(); 
  
 
  function updateUrlWithPage(page: number) {
    isNavigatingProgrammatically = true;
    const pageQuery = page > 1 ? `?page=${page}` : '';
    push(`#/PlayerList${pageQuery}`);
    setTimeout(() => {
      isNavigatingProgrammatically = false;
    }, 100);
  }
  
  
  type PromoAction =
  {
    action: "promo",
    userid: number
  };

  type WithdrawAction =
  {
    action: "withdraw",
    userid: number
  };

  type WithdrawcashierAction =
  {
    action: "withdrawcashier",
    userid: number
  };


  type DepositAction =
  {
    action: "deposit",
    userid: number;
  };

  type EditAction =
  {
    action: "edit",
    userid: number;
  };

  type AddAction =
  {
    action: "add"
  };

  type RouteParams =
    WithdrawAction |
    WithdrawcashierAction |
    DepositAction |
    EditAction |
    AddAction |
    PromoAction |
    undefined;



  export let params: RouteParams = undefined;

  let selectedUser: User | undefined = undefined;
  $: if (params)
  {
    if (params.action == "deposit" ||
      params.action == "promo" ||
      params.action == "withdraw" ||
      params.action == "withdrawcashier" || 
      params.action == "edit")
    {
      let selectedUserId = params.userid;
      selectedUser = get(usersByRole[UserRole.Player]).find(p => p.Id == selectedUserId);
    }
  }

  let allPlayers: User[] = [];
  let searchPlayers: User[] = [];
  let searchInput: string = "";

  let message = { text: '', type: '' };

  function readPageFromUrl(): number | null {
    const hash = window.location.hash;
    const hashParts = hash.split('?');
    let pageParam = null;
    
    if (hashParts.length > 1) {
      const hashQueryParams = new URLSearchParams(hashParts[1]);
      pageParam = hashQueryParams.get('page');
    }
    
    if (pageParam) {
      const pageNumber = parseInt(pageParam, 10);
      if (pageNumber > 0) {
        return pageNumber;
      }
    }
    return null;
  }

  onMount(() => {
    if (!$me) {
      push("/user/Login");
    }
    
    const pageFromUrl = readPageFromUrl();
    if (pageFromUrl) {
      currentPage = pageFromUrl;
      lastKnownPage = pageFromUrl;
    }
  });

  
  $: {
    if ($location && !isNavigatingProgrammatically) {
      const pageFromUrl = readPageFromUrl();
      
      if (pageFromUrl) {
        if (pageFromUrl !== currentPage) {
          currentPage = pageFromUrl;
          lastKnownPage = pageFromUrl;
        }
      } else {
        // Solo resetear a página 1 si realmente estamos en /PlayerList sin parámetros
        // y no hay una acción activa, pero solo si no estamos en medio de una carga inicial
        if ($location === '/PlayerList' && !params?.action && searchPlayers.length > 0) {
          if (currentPage !== 1) {
            currentPage = 1;
            lastKnownPage = 1;
          }
        } else if (currentPage !== lastKnownPage && lastKnownPage > 0) {
          currentPage = lastKnownPage;
        }
      }
    }
  }

  onMount(async () => {
    await RefreshUsers();
    // Después de cargar usuarios, verificar si hay página en la URL y preservarla
    const pageFromUrl = readPageFromUrl();
    if (pageFromUrl && pageFromUrl !== currentPage) {
      currentPage = pageFromUrl;
      lastKnownPage = pageFromUrl;
    }
    
    // Asegurar que searchPlayers se inicialice si hay datos pero aún no se ha inicializado
    if (allPlayers.length > 0 && searchPlayers.length === 0) {
      updatePlayersWithoutResettingPage();
    }
  });


  usersByRole[UserRole.Player].subscribe((players) => {
    allPlayers = players;
  
    // Asegurar que searchPlayers se inicialice correctamente
    if (players.length > 0) {
      updatePlayersWithoutResettingPage();
    } else {
      searchPlayers = [];
      paginatedPlayers = [];
    }
  });

  // Reactive statement para asegurar que searchPlayers se inicialice cuando allPlayers o $me tienen datos
  $: {
    if ($me && allPlayers.length > 0 && searchPlayers.length === 0 && !searchInput) {
      updatePlayersWithoutResettingPage();
    }
  }
  
  function isTransactionInProgress(transaction: WalletTransaction): boolean {
    return transaction.Type === WalletTransactionType.WithdrawWaiting;
  }


  function shouldUpdatePlayerForTransaction(
    transaction: WalletTransaction,
    player: User,
    isNewTransaction: boolean
  ): boolean {

    if (isNewTransaction) {
      return true;
    }
    

    if (isTransactionInProgress(transaction)) {
      return true;
    }
    

    if (player.LastWalletTransactionId === transaction.Id) {
      const currentBalance = player.LastWalletTransaction?.CashableBalance?.InCents ?? 0;
      const newBalance = transaction.CashableBalance?.InCents ?? 0;
      if (currentBalance !== newBalance) {
        return true;
      }
    }
    
    return false;
  }

  $: {
    if ($Transactions.length > 0) {
      setTimeout(() => {
        const newTransactionIds = new Set<number>();
        const inProgressTransactionIds = new Set<number>();
        const affectedUserIds = new Set<number>();
        
        $Transactions.forEach(transaction => {
          const isNew = !processedTransactionIds.has(transaction.Id);
          if (isNew) {
            newTransactionIds.add(transaction.Id);
            affectedUserIds.add(transaction.UserId);
          }
          
          if (isTransactionInProgress(transaction)) {
            inProgressTransactionIds.add(transaction.Id);
            affectedUserIds.add(transaction.UserId);
          }
        });
        
        if (newTransactionIds.size === 0 && inProgressTransactionIds.size === 0) {
          return;
        }
        
        newTransactionIds.forEach(id => processedTransactionIds.add(id));
        
        
        if (processedTransactionIds.size > 200) {
          const currentIds = new Set($Transactions.map(t => t.Id));
          processedTransactionIds = new Set(
            Array.from(processedTransactionIds).filter(id => currentIds.has(id))
          );
        }
        
        // Solo procesar jugadores afectados
        usersByRole[UserRole.Player].update(players => {
          let hasUpdates = false;
          const updatedPlayers = players.map(player => {
            // Solo procesar jugadores afectados
            if (!affectedUserIds.has(player.Id)) {
              return player;
            }
            
            // Obtener la última transacción del jugador
            const playerTransactions = $Transactions.filter(t => t.UserId === player.Id);
            const lastTransaction = playerTransactions.length > 0 ? playerTransactions[0] : null;
            
            if (!lastTransaction) {
              return player;
            }
            
            const isNewTransaction = newTransactionIds.has(lastTransaction.Id);
            const isInProgress = inProgressTransactionIds.has(lastTransaction.Id);
            const shouldUpdate = shouldUpdatePlayerForTransaction(
              lastTransaction,
              player,
              isNewTransaction || isInProgress
            );
            
            if (!shouldUpdate) {
              return player;
            }
            
            const currentPromotions = player.LastWalletTransaction?.PromotionTransactions || [];
            const newPromotions = lastTransaction.PromotionTransactions || [];
            
            // Usar directamente las promociones que envía el backend
            const finalPromotions = newPromotions || [];
            
            const promotionsChanged = JSON.stringify(currentPromotions) !== JSON.stringify(newPromotions);
            const transactionIdChanged = player.LastWalletTransactionId !== lastTransaction.Id;
            const balanceChanged = player.LastWalletTransaction?.CashableBalance?.InCents !== lastTransaction.CashableBalance?.InCents;
            
            if (promotionsChanged || transactionIdChanged || balanceChanged) {
              hasUpdates = true;
              return {
                ...player,
                LastWalletTransactionId: lastTransaction.Id,
                LastWalletTransaction: {
                  ...lastTransaction,
                  PromotionTransactions: finalPromotions
                }
              };
            }
            
            return player;
          });
          
          if (hasUpdates) {
            return updatedPlayers;
          }
          
          return players; 
        });
      }, 0);
    }
  }

  function updatePagination() {
    const startIndex = (currentPage - 1) * playersPerPage;
    const endIndex = startIndex + playersPerPage;
    paginatedPlayers = searchPlayers.slice(startIndex, endIndex);
   
  }

  // Reactive statement para actualizar automáticamente la paginación
  $: {
    if (searchPlayers && currentPage) {
      const startIndex = (currentPage - 1) * playersPerPage;
      const endIndex = startIndex + playersPerPage;
      paginatedPlayers = searchPlayers.slice(startIndex, endIndex);
      
      // Ajustar currentPage si está fuera de rango, pero solo si hay datos cargados
      // y no estamos preservando una página de la URL
      const totalPages = Math.ceil(searchPlayers.length / playersPerPage);
      if (totalPages > 0 && currentPage > totalPages) {
        // Verificar si la página actual viene de la URL antes de ajustarla
        const pageFromUrl = readPageFromUrl();
        if (!pageFromUrl || pageFromUrl > totalPages) {
          currentPage = totalPages;
          lastKnownPage = totalPages;
          updateUrlWithPage(totalPages);
        }
      }
    }
  }

  // Variable reactiva para totalPages
  $: totalPages = Math.ceil(searchPlayers.length / playersPerPage);

  function getTotalPages(): number {
    return totalPages;
  }

  function goToPage(page: number) {
   
    if (page >= 1 && page <= getTotalPages()) {
      currentPage = page;
      lastKnownPage = page;
      updatePagination();
      updateUrlWithPage(page);
    }
  }

  function previousPage() {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  }

  function nextPage() {
    if (currentPage < getTotalPages()) {
      goToPage(currentPage + 1);
    }
  }

  function getPageRange() {
    const totalPages = getTotalPages();
    const current = currentPage;
    const delta = 2; 
    
    let start = Math.max(1, current - delta);
    let end = Math.min(totalPages, current + delta);
    
    if (end - start < 4) {
      if (start === 1) {
        end = Math.min(totalPages, start + 4);
      } else {
        start = Math.max(1, end - 4);
      }
    }
    
    return Array.from({length: end - start + 1}, (_, i) => start + i);
  }

  function isPlayerInUserHierarchy(player: User, user: User): boolean {
    if (!player.GroupId || !user.GroupId) {
      return false;
    }

    if (player.GroupId === user.GroupId) {
      return true;
    }

    const userGroup = $groups.find(g => g.Id === user.GroupId);
    if (userGroup?.ParentId && player.GroupId === userGroup.ParentId) {
      return true;
    }

    if (player.Group?.ParentId === user.GroupId) {
      return true;
    }

    let currentGroup = player.Group;
    while (currentGroup?.ParentId) {
      if (currentGroup.ParentId === user.GroupId) {
        return true;
      }
      currentGroup = $groups.find(g => g.Id === currentGroup?.ParentId);
    }

    return false;
  }

  function isPlayerInCashierHierarchy(player: User, cashier: User): boolean {
    if (!player.GroupId || !cashier.GroupId) {
      return false;
    }

    const playerGroupId = Number(player.GroupId);
    const cashierGroupId = Number(cashier.GroupId);

    // Si el jugador está en el mismo grupo que el cajero, permitir
    if (playerGroupId === cashierGroupId) {
      return true;
    }

    // Verificar si el jugador está en un grupo descendiente (hijo, nieto, etc.) del grupo del cajero
    // Recorremos hacia arriba desde el grupo del jugador hasta encontrar el grupo del cajero
    let currentGroup = player.Group;
    while (currentGroup?.ParentId) {
      const parentId = Number(currentGroup.ParentId);
      
      // Si encontramos que el grupo del cajero es ancestro del grupo del jugador, permitir
      if (parentId === cashierGroupId) {
        return true;
      }
      
      // Continuar subiendo en la jerarquía
      currentGroup = $groups.find(g => g.Id === parentId);
    }

    // Si no encontramos el grupo del cajero en la cadena de ancestros, no permitir
    // Esto significa que el jugador está en un grupo padre, hermano o sin relación
    return false;
  }

  function filterPlayers()
  {

    let filteredByGroup = allPlayers;
    
    if ($me?.Role === UserRole.Admin) {
      filteredByGroup = allPlayers.filter(player => {
        const isInHierarchy = isPlayerInUserHierarchy(player, $me);
        return isInHierarchy;
      });
    } else if ($me?.Role === UserRole.Cashier) {
      filteredByGroup = allPlayers.filter(player => {
        const isInHierarchy = isPlayerInCashierHierarchy(player, $me);
        return isInHierarchy;
      });
    } else if ($me?.Role === UserRole.Owner) {
      filteredByGroup = allPlayers;
    }

    if (searchInput == '')
    {
      searchPlayers = filteredByGroup;
    }
    else
    {
      let searchInputNormalized = searchInput.toLowerCase();
      searchPlayers = filteredByGroup.filter((player) =>
        player?.Id?.toString().toLowerCase()?.includes(searchInputNormalized) ||
        player?.Role?.toString().toLowerCase()?.includes(searchInputNormalized) ||
        player?.Email?.toLowerCase()?.includes(searchInputNormalized) ||
        player?.FirstName?.toLowerCase()?.includes(searchInputNormalized) ||
        player?.LastName?.toLowerCase()?.includes(searchInputNormalized)
      );
    }
    
    const totalPages = Math.ceil(searchPlayers.length / playersPerPage);
    // Solo ajustar la página si está fuera de rango y no viene de la URL
    if (currentPage > totalPages && totalPages > 0) {
      const pageFromUrl = readPageFromUrl();
      if (!pageFromUrl || pageFromUrl > totalPages) {
        currentPage = totalPages;
        lastKnownPage = totalPages;
      }
    }
    updatePagination();
  }

  function updatePlayersWithoutResettingPage()
  {
    let filteredByGroup = allPlayers;
    
    if ($me?.Role === UserRole.Admin) {
      filteredByGroup = allPlayers.filter(player => {
        const isInHierarchy = isPlayerInUserHierarchy(player, $me);
        return isInHierarchy;
      });
    } else if ($me?.Role === UserRole.Cashier) {
      filteredByGroup = allPlayers.filter(player => {
        const isInHierarchy = isPlayerInCashierHierarchy(player, $me);
        return isInHierarchy;
      });
    } else if ($me?.Role === UserRole.Owner) {
      filteredByGroup = allPlayers;
    }

    if (searchInput == '')
    {
      searchPlayers = filteredByGroup;
    }
    else
    {
      let searchInputNormalized = searchInput.toLowerCase();
      searchPlayers = filteredByGroup.filter((player) =>
        player?.Id?.toString().toLowerCase()?.includes(searchInputNormalized) ||
        player?.Role?.toString().toLowerCase()?.includes(searchInputNormalized) ||
        player?.Email?.toLowerCase()?.includes(searchInputNormalized) ||
        player?.FirstName?.toLowerCase()?.includes(searchInputNormalized) ||
        player?.LastName?.toLowerCase()?.includes(searchInputNormalized)
      );
    }
    
    updatePagination();
  }

  function dismiss()
  {
    const pageQuery = currentPage > 1 ? `?page=${currentPage}` : '';
    push(`#/PlayerList${pageQuery}`);
  }




  $: {
    searchInput;
    searchChanged();
  }

  function clearSearch() {
    searchInput = '';
  }


  let searchTimer: number = 0;
  function searchChanged()
  {
    if (searchTimer != 0) window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(applySearch, 500)
  }

  function applySearch()
  {
    searchTimer = 0;
    filterPlayers();
  }

  function IsSearchAndNoPlayersMatch(searchPlayes: User[])
  {
    if (searchInput && searchPlayers.length == 0)
    {
      return true;
    }

    return false;
  }

  function IsSearch(searchInput: string)
  {
    return searchInput && searchInput.length > 0;
  }

  async function DisablePlayer(user: User) {
    try {
      user.Enabled = !user.Enabled;
      
      const result = await service.walletapi.disableUser({
        Token: GetCachedToken(),
        Data: {
          Enabled: user.Enabled,
          Id: user.Id
        }
      });

      if (result.Success) {
        const players = get(usersByRole[UserRole.Player]);
        const updatedPlayers = players.map(p => 
          p.Id === user.Id ? { ...p, Enabled: user.Enabled } : p
        );
        usersByRole[UserRole.Player].set(updatedPlayers);

        message = {
          text: `Player ${user.FirstName} has been ${user.Enabled ? 'enabled' : 'disabled'} successfully`,
          type: user.Enabled ? 'success' : 'negative'
        };
      } else {
        user.Enabled = !user.Enabled;
        message = {
          text: result.Message || 'Error updating player status',
          type: 'error'
        };
      }
    } catch (err) {
      // Si hay error, revertir el cambio
      user.Enabled = !user.Enabled;
      message = {
        text: err instanceof Error ? err.message : 'An unexpected error occurred',
        type: 'error'
      };
    }

    setTimeout(() => {
      message = { text: '', type: '' };
    }, 3000);
  }

  function CanWithdraw(lastTransaction: WalletTransaction | undefined): boolean
  {
    if (!lastTransaction)
    {
      return false;
    }

    const moneyTransactionBalance = lastTransaction.MoneyTransaction?.[0]?.Balance?.InCents ?? 0;
    if (moneyTransactionBalance > 0)
    {
      return true;
    }

    const cashableBalance = lastTransaction.CashableBalance?.InCents ?? 0;
    if (cashableBalance > 0)
    {
      return true;
    }

    return false;
  }

  function CanDeposit(user: User): boolean
  {
    if (!user.Enabled)
    {
      return false;
    }

    return true;
  }

  function CanWithdrawForUser(user: User, lastTransaction: WalletTransaction | undefined): boolean
  {
    if (!user.Enabled)
    {
      return false;
    }

    return CanWithdraw(lastTransaction);
  }

  function sumPromotionBalances(promoArr: any[]) {
    if (!promoArr || promoArr.length === 0) return 0;
    return promoArr.reduce((sum: number, promo: any) => sum + (promo.Balance?.InCents || 0), 0);
  }


  function getGroupPath(group: any): string {
    if (!group || !$me?.GroupId) return group?.Name || '';
    
    const myGroupId = Number($me.GroupId);
    const playerGroupId = Number(group.Id);
    
    // Si el jugador está en mi mismo grupo
    if (playerGroupId === myGroupId) {
      return '📁';
    }
    
    // Construir la jerarquía completa del grupo del jugador (de arriba hacia abajo)
    let pathParts: string[] = [];
    let currentGroup: any = group;
    
    // Primero construir el array de grupos desde el jugador hasta la raíz
    while (currentGroup) {
      if (currentGroup.Name) {
        pathParts.unshift(currentGroup.Name);
      }
      if (currentGroup.ParentId) {
        currentGroup = $groups.find(g => g.Id === currentGroup.ParentId);
      } else {
        break;
      }
    }
    
    // Buscar si mi grupo está en la jerarquía del jugador
    let myGroupIndex = -1;
    currentGroup = group;
    
    // Recorrer desde el grupo del jugador hacia arriba buscando mi grupo
    let index = pathParts.length - 1;
    while (currentGroup) {
      const currentGroupId = Number(currentGroup.Id);
      if (currentGroupId === myGroupId) {
        myGroupIndex = index;
        break;
      }
      if (currentGroup.ParentId) {
        currentGroup = $groups.find(g => g.Id === currentGroup.ParentId);
        index--;
      } else {
        break;
      }
    }
    
    // Si mi grupo está en la jerarquía, reemplazar con "📁" y mantener los subgrupos
    if (myGroupIndex >= 0) {
      // Tomar solo desde mi grupo hacia abajo y reemplazar el nombre de mi grupo con "📁"
      const subPath = pathParts.slice(myGroupIndex);
      subPath[0] = '📁';
      return subPath.join(' └─ ');
    }
    
    // Si no hay relación, mostrar según el rol
    if ($me?.Role === UserRole.Cashier) {
      return group.Name || '';
    }
    
    // Para Admin, mostrar la jerarquía completa con el separador
    return pathParts.join(' └─ ');
  }

</script>


<h1 class="ui centered dividing header">{$translate('List of players')} ({searchPlayers?.length ?? ""})</h1>


<div class="ui action input">
  <input type="text" bind:value={searchInput} placeholder={$translate('Player name')}  autocomplete="off">
  <button class="ui icon button" on:click={clearSearch}>
    <i class="times icon"></i>
  </button>
</div>
{#if params?.action == "promo"}
  <Modal dismiss={dismiss}>
    {#if !selectedUser}
      <div class="ui negative message" on:click={dismiss}>
        <i class="close icon"></i>
        <div class="header">
          {$translate('No such User')}
        </div>
        <p>Player with id {params?.userid ?? "?"}  {$translate('does not exist')}</p>
      </div>
    {:else}
      <PromotionalcreditsForm player={selectedUser} dismiss={dismiss} type="promo"></PromotionalcreditsForm>
    {/if}
  </Modal>
{/if}
{#if params?.action == "deposit"}
  <Modal dismiss={dismiss}>
    {#if !selectedUser}
      <div class="ui negative message" on:click={dismiss}>
        <i class="close icon"></i>
        <div class="header">
          {$translate('No such User')}
        </div>
        <p>Player with id {params?.userid ?? "?"} {$translate('does not exist')}</p>
      </div>
    {:else if !CanDeposit(selectedUser)}
      <div class="ui negative message">
        <i class="close icon" on:click={dismiss}></i>
        <div class="header">
          {$translate('User Disabled')}
        </div>
        <p>{$translate('Cannot make deposits for disabled users')}</p>
        <button class="ui button" on:click={dismiss}>{$translate('Close')}</button>
      </div>
    {:else}
      <WithdrawDepositForm player={selectedUser} dismiss={dismiss} type="deposit"></WithdrawDepositForm>
    {/if}
  </Modal>
{/if}

{#if params?.action == "withdraw"}
  <Modal dismiss={dismiss}>
    {#if !selectedUser}
      <div class="ui negative message">
        <div class="header">{$translate('No such User')}</div>
      </div>
    {:else if !CanWithdrawForUser(selectedUser, selectedUser.LastWalletTransaction)}
      <div class="ui negative message">
        <i class="close icon" on:click={dismiss}></i>
        <div class="header">
          {$translate('User Disabled or No Balance')}
        </div>
        <p>{$translate('Cannot make withdrawals for disabled users or users without balance')}</p>
        <button class="ui button" on:click={dismiss}>{$translate('Close')}</button>
      </div>
    {:else}
      <WithdrawDepositForm player={selectedUser} dismiss={dismiss} type="withdraw"></WithdrawDepositForm>
    {/if}
  </Modal>
{/if}

{#if params?.action == "withdrawcashier"}
  <Modal dismiss={dismiss}>
    {#if !selectedUser}
      <div class="ui negative message">
        <div class="header">{$translate('No such User')}</div>
      </div>
    {:else if !CanWithdrawForUser(selectedUser, selectedUser.LastWalletTransaction)}
      <div class="ui negative message">
        <i class="close icon" on:click={dismiss}></i>
        <div class="header">
          {$translate('User Disabled or No Balance')}
        </div>
        <p>{$translate('Cannot make withdrawals for disabled users or users without balance')}</p>
        <button class="ui button" on:click={dismiss}>{$translate('Close')}</button>
      </div>
    {:else}
      <WithdrawCashierForm player={selectedUser} dismiss={dismiss}></WithdrawCashierForm>
    {/if}
  </Modal>
{/if}

{#if params?.action == "add"}
  <Modal dismiss={dismiss} size="large">
    <AddPlayer dismiss={dismiss}></AddPlayer>
  </Modal>
{/if}

{#if params?.action == "edit"}
  <Modal dismiss={dismiss} size="large">
    {#if !selectedUser}
      <div class="ui negative message">
        <div class="header">{$translate('No such User')}</div>
      </div>
    {:else}
      <EditUserForm user={selectedUser} dismiss={dismiss}></EditUserForm>
    {/if}
  </Modal>
{/if}

{#if IsSearchAndNoPlayersMatch(searchPlayers)}
  <div class="ui negative message">
    <div class="header">{$translate('No players found')}</div>
  </div>
{:else}

<a href="#/PlayerList/add" class="ui green button">
  <i class="user plus icon"></i>
 {$translate('Add player')}
</a> 
<div style="overflow-x: auto;">
<table class="playerlist ui celled unstackable striped selectable very compact unselectable table">
  <thead>
    <tr>
      <th style="width: 0px">#</th>
      <th>{$translate('First Name')}</th>
      <th>{$translate('E-mail')}</th>
      <th>{$translate('Phone Number')}</th>
      <th>{$translate('Birthday')}</th>
      <th>{$translate('Language')}</th>
      <th>{$translate('Group')}</th>
      <th>{$translate('Promo')}</th>
      <th>{$translate('Balance')}</th>
      <th>{$translate('Total')}</th>
      <th style="width: 0px">Allow</th>
      <th style="width: 200px">{$translate('Options')}</th>
    </tr>
  </thead>
  <tbody>
    {#each paginatedPlayers as user}
      <tr>
        <td class="numeric">{user.Id}</td>
        <td class="name-column" title="{user.FirstName} {user.LastName}">
          {user.FirstName} {user.LastName}
        </td>
        <td class="email-column" title={user.Email}>
          {user.Email ?? ""}
        </td>
        <td class="phone-column" title={user.PhoneNumber}>
          {user.PhoneNumber ?? ""}
        </td>
        <td class="phone-column" title={user.BirthdayDay?.toString()}>
         {formatDate(user.BirthdayDay)}
        </td>
        <td class="phone-column" title={user.Language || '-'}>
          {user.Language?.toUpperCase() || '-'}
        </td>
        <td class="group-column" title={getGroupPath(user.Group)}>
          <span class="my-group-badge">{getGroupPath(user.Group)}</span>
        </td>
        <td class="numeric balance-column">
          {formatMoney(sumPromotionBalances(user.LastWalletTransaction?.PromotionTransactions || []))}
        </td>
        <td class="numeric balance-column">
         {formatMoney(user.LastWalletTransaction?.MoneyTransaction?.[0]?.Balance|| 0)}
        </td>
        <td class="numeric balance-column">
          {formatMoney(user.LastWalletTransaction?.CashableBalance || 0)}
        </td>
        <td>
          <button class={`ui icon small button ${user.Enabled ? 'positive' : 'negative'}`} on:click={() => DisablePlayer(user)}>
            {#if user.Enabled}
              <i class="check icon"></i>
            {:else}
              <i class="close icon"></i>
            {/if}
          </button>
        </td>
        <td>
          <div style="display: inline-flex; flex-wrap: nowrap; gap: 4px;">
            <button class="ui positive small button" class:disabled={!CanDeposit(user)} on:click={() => push(`#/PlayerList/deposit/${user.Id}?page=${currentPage}`)}>{$translate('Deposit')}</button>
            {#if $me?.Role === 'Cashier'}
              <a class="ui negative small button" class:disabled={!CanWithdrawForUser(user, user.LastWalletTransaction)} href={`#/PlayerList/withdrawcashier/${user.Id}?page=${currentPage}`}>{$translate('Withdraw')}</a>
            {:else}
              <a class="ui negative small button" class:disabled={!CanWithdrawForUser(user, user.LastWalletTransaction)} href={`#/PlayerList/withdraw/${user.Id}?page=${currentPage}`}>{$translate('Withdraw')}</a>
            {/if}
            <a class="ui teal small button" href="#/UserbyTransaction/view/{user.Id}">{$translate('Transactions')}</a>
            <a class="ui primary icon small button" href={`#/playerlist/edit/${user.Id}?page=${currentPage}`}>
              <i class="edit icon"></i>
            </a>
          </div>
        </td>
      </tr>
    {/each}
  </tbody>
</table>
</div>

{#if totalPages > 1}
  <div class="ui pagination menu" style="justify-content: center; margin-top: 1em;">
    <a class="icon item" class:disabled={currentPage === 1} on:click={previousPage}>
      <i class="left chevron icon"></i>
    </a>
    
    {#if currentPage > 3}
      <a class="item" on:click={() => goToPage(1)}>1</a>
      {#if currentPage > 4}
        <div class="disabled item">...</div>
      {/if}
    {/if}
    
    {#each getPageRange() as page}
      <a class="item" class:active={page === currentPage} on:click={() => goToPage(page)}>
        {page}
      </a>
    {/each}
    
    {#if currentPage < getTotalPages() - 2}
      {#if currentPage < getTotalPages() - 3}
        <div class="disabled item">...</div>
      {/if}
      <a class="item" on:click={() => goToPage(getTotalPages())}>{getTotalPages()}</a>
    {/if}
    
    <a class="icon item" class:disabled={currentPage === getTotalPages()} on:click={nextPage}>
      <i class="right chevron icon"></i>
    </a>
  </div>
  
  <div class="ui center aligned text" style="margin-top: 0.5em; color: #666;">
    displaying  {((currentPage - 1) * playersPerPage) + 1} - {Math.min(currentPage * playersPerPage, searchPlayers.length)} of {searchPlayers.length} players
  </div>
{/if}
{/if}

{#if message.text}
  <div class="ui message {message.type === 'success' ? 'positive' : 'negative'}" transition:fade>
    <i class="close icon" on:click={() => message = { text: '', type: '' }}></i>
    <div class="header">
      {#if message.type === 'success'}
        <i class="check circle icon"></i> Success
      {:else if message.type === 'negative'}
        <i class="ban icon"></i> Player Disabled
      {:else}
        <i class="exclamation triangle icon"></i> Error
      {/if}
    </div>
    <p>{message.text}</p>
  </div>
{/if}

<style>
  .center{
    text-align: center;
  }

  .ui.input {
    margin-bottom: 1em;
  }

  .playerlist {
    white-space: nowrap;
    font-size: 0.85em !important;
  }

  .playerlist td {
    padding: 0.4em !important;
    text-align: center;
  }

  .playerlist th {
    padding: 0.3em 0.4em !important;
    text-align: center;
  }

  .numeric {
    font-family: monospace;
    text-align: right !important;
  }

  .ui.small.button {
    padding: 0.6em 0.8em !important;
    font-size: 0.85em !important;
    margin: 0 !important;
  }

  .ui.table td {
    padding: 0.3em !important;
  }

  /* Ajustar anchos de columnas específicas */
  .id-column {
    width: 40px !important;
  }

  .name-column {
    width: 100px !important;
  }

  .email-column {
    width: 150px !important;
  }

  .phone-column {
    width: 80px !important;
  }

  .balance-column {
    width: 70px !important;
  }

  .group-column {
    width: 180px !important;
    text-align: left !important;
    max-width: 180px !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .my-group-badge {
    background-color: #e3f2fd;
    color: #1976d2;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.8em;
    font-weight: bold;
    border: 1px solid #2196f3;
  }

  .child-group-badge {
    background-color: #f3e5f5;
    color: #7b1fa2;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.8em;
    border: 1px solid #9c27b0;
  }

  .parent-group-badge {
    background-color: #fff3e0;
    color: #f57c00;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.8em;
    border: 1px solid #ff9800;
  }

  .hierarchy-group-badge {
    background-color: #e8f5e8;
    color: #2e7d32;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.8em;
    font-weight: bold;
    border: 1px solid #4caf50;
  }

  .other-group-badge {
    background-color: #f5f5f5;
    color: #666;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.8em;
    border: 1px solid #ddd;
  }

  .options-column {
    width: 220px !important;
    white-space: nowrap;
    padding: 0.3em !important;
  }

  .ui.message {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000;
    margin: 0;
    padding: 1.5em;
    min-width: 300px;
    max-width: 500px;
    text-align: center;
    box-shadow: 0 3px 6px rgba(0,0,0,0.16);
    border-radius: 8px;
  }

  .ui.message .close.icon {
    position: absolute;
    top: 0.8em;
    right: 0.8em;
    cursor: pointer;
    opacity: 0.7;
  }

  .ui.message .close.icon:hover {
    opacity: 1;
  }

  .ui.message .header {
    margin-bottom: 0.5em;
    font-weight: bold;
  }

  /* Estilos para la paginación */
  .ui.pagination.menu {
    border-radius: 0.28571429rem;
    box-shadow: 0 1px 2px 0 rgba(34, 36, 38, 0.15);
  }

  .ui.pagination.menu .item {
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .ui.pagination.menu .item:hover:not(.disabled) {
    background-color: #f9fafb;
  }

  .ui.pagination.menu .item.active {
    background-color: #2185d0;
    color: white;
    font-weight: bold;
  }

  .ui.pagination.menu .item.disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .ui.pagination.menu .item.disabled:hover {
    background-color: transparent;
  }
  @media (max-width: 768px) {
    .playerlist th,
    .playerlist td {
      font-size: 12px;
      padding: 6px;
    }

    .playerlist .ui.button {
      font-size: 10px;
      padding: 4px 6px;
    }

    .playerlist .name-column,
    .playerlist .email-column,
    .playerlist .phone-column,
    .playerlist .group-column {
      max-width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .playerlist .ui.small.button {
      font-size: 9px !important;
      padding: 3px 4px !important;
    }
  }
</style>